// /login (PRD §9.4 + docs/frontend_architecture.md §6, B1e PR4).
//
// react-hook-form + zod for client-side validation; the zod schema is
// typed against `components['schemas']['LoginRequest']` so the form
// stays in lockstep with the backend contract via the codegen pipeline.
// Submission goes through the useLogin() mutation, which delegates to
// auth.ts (fills the in-memory store + fetches /me), then we hard-nav
// to the redirect target so the route guard's bootstrap path runs
// cleanly under the new session.
//
// Errors:
//   - 401 (bad credentials)         → root form error.
//   - 422 (RequestValidationError)  → per-field setError matched on
//                                     the trailing element of `loc`.
//   - anything else                 → generic root error.

import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { redirectAfterLogin } from '../api/authNavigation';
import { ApiError } from '../api/client';
import { useLogin } from '../api/mutations/auth';
import type { components } from '../api/schema';
import { useAuthStore } from '../stores/authStore';

// Inferring the form-values type from the zod schema (rather than
// annotating the schema as `z.ZodType<LoginRequest>`) keeps the
// resolver's input/output types intact. The `Equal` gate below holds
// the inferred shape against the OpenAPI contract: if the backend's
// LoginRequest grows or renames a field, `LoginFormValues` collapses
// to a string-literal sentinel, which breaks the resolver call below
// with a useful compile-time message rather than a misleading runtime
// shape mismatch.
const loginSchema = z.object({
  email: z.string().email({ message: 'Enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

type Equal<X, Y> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2
    ? true
    : false;

type LoginFormValues =
  Equal<z.infer<typeof loginSchema>, components['schemas']['LoginRequest']> extends true
    ? z.infer<typeof loginSchema>
    : 'ERROR: login form schema drifted from backend LoginRequest';

interface LoginSearch {
  redirect?: string;
}

/**
 * Defang the `redirect` query param so a crafted
 * `?redirect=https://evil.example` can't bounce the user off-site.
 * Anything that isn't a same-origin path (single leading `/`) collapses
 * to the home route.
 */
function safeRedirectTarget(target: string | undefined): string {
  if (!target) return '/';
  if (!target.startsWith('/') || target.startsWith('//')) return '/';
  return target;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const r = search['redirect'];
    return typeof r === 'string' ? { redirect: r } : {};
  },
  beforeLoad: ({ search }) => {
    // Already signed in? Bounce to the redirect target so navigating
    // back to /login doesn't trap the user on a useless form.
    if (useAuthStore.getState().accessToken !== null) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: safeRedirectTarget(search.redirect) });
    }
  },
  component: LoginRoute,
});

function LoginRoute(): React.JSX.Element {
  const search = Route.useSearch();
  return <LoginForm redirectTarget={safeRedirectTarget(search.redirect)} />;
}

interface LoginFormProps {
  /** Validated, same-origin path the user lands on after a successful
   *  login. Pass through `safeRedirectTarget` upstream so this component
   *  can stay a dumb consumer. */
  redirectTarget: string;
}

/**
 * Pulled out from the route binding so tests don't need a TanStack
 * Router context to drive the form. The route component (above) is the
 * only production caller; tests render this directly inside a
 * QueryClientProvider.
 */
export function LoginForm({ redirectTarget }: LoginFormProps): React.JSX.Element {
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await loginMutation.mutateAsync(values);
      redirectAfterLogin(redirectTarget);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError('root', { message: 'Email or password is incorrect.' });
          return;
        }
        if (err.status === 422 && Array.isArray(err.detail)) {
          for (const issue of err.detail) {
            const field = issue.loc[issue.loc.length - 1];
            if (field === 'email' || field === 'password') {
              setError(field, { message: issue.msg });
            }
          }
          return;
        }
      }
      setError('root', { message: 'Something went wrong. Please try again.' });
    }
  });

  return (
    <div className="mx-auto max-w-md p-7">
      <header className="mb-7">
        <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
          KansiTRK · web
        </p>
        <h1 className="text-text text-28 font-semibold">Sign in</h1>
      </header>

      <form
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="email" className="text-text-muted text-13">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className="bg-bg-elevated text-text border-border rounded-2 focus:border-accent border px-3 py-2 text-13 outline-none"
            {...register('email')}
          />
          {errors.email && (
            <p id="email-error" role="alert" className="text-danger text-11">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="password" className="text-text-muted text-13">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.password ? 'true' : 'false'}
            aria-describedby={errors.password ? 'password-error' : undefined}
            className="bg-bg-elevated text-text border-border rounded-2 focus:border-accent border px-3 py-2 text-13 outline-none"
            {...register('password')}
          />
          {errors.password && (
            <p id="password-error" role="alert" className="text-danger text-11">
              {errors.password.message}
            </p>
          )}
        </div>

        {errors.root && (
          <p role="alert" className="text-danger text-13">
            {errors.root.message}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi border px-3 py-2 text-13 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
